import React, { useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Alert,
  Divider,
  List,
  Snackbar,
  CircularProgress,
  Stack,
  Tooltip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { YouTube, Instagram, Facebook, Audiotrack } from "@mui/icons-material";
import TikTokIcon from "../components/icons/TikTokIcon";
import SpotifyIcon from "../components/icons/SpotifyIcon";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useColorMode } from "../contexts/ColorModeContext";
import { useQuery } from "@tanstack/react-query";
import apiService, { Podcast, SocialAccount } from "../services/api";
import ChangePasswordDialog from "../components/ChangePasswordDialog";
import { settingsRowActionButtonSx } from "../components/settings/settingsRowActionButtonSx";
import { getS3Url, addCacheBuster } from "../utils/imageUtils";

// Function to ensure direct S3 URL access
const ensureDirectS3Url = (url: string): string => {
  if (!url) return url;
  // If it's already a direct S3 URL, return as is
  if (url.includes("podscope.s3.amazonaws.com")) {
    return url;
  }
  // If it's a relative path, make it absolute
  if (url.startsWith("/")) {
    return `https://podscope.s3.amazonaws.com${url}`;
  }
  return url;
};

const Settings: React.FC = () => {
  const { user } = useAuth();
  const { mode, setMode } = useColorMode();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);

  // Load company and logged user's podcasts
  const { data: myCompanyResp } = useQuery({
    queryKey: ["settings-my-company", user?.companyId],
    queryFn: () => apiService.getMyCompany(),
    staleTime: 1000 * 60 * 5, // 5 min
    gcTime: 1000 * 60 * 5, // 5 min
  });
  const myCompanyId = (myCompanyResp as any)?._id as string | undefined;
  const {
    data: myPodcastsResp,
    isLoading: myPodcastsLoading,
    error: myPodcastsError,
  } = useQuery({
    queryKey: ["settings-company-podcasts", myCompanyId],
    queryFn: () =>
      apiService.getCompanyPodcasts(myCompanyId || "", {
        includePrograms: true,
      }),
    enabled: !!myCompanyId,
    staleTime: 1000 * 60 * 5, // 5 min
    gcTime: 1000 * 60 * 5, // 5 min
    placeholderData: (previousData) => previousData,
  });
  const myPodcasts: Podcast[] = (myPodcastsResp as any)?.podcasts || [];
  const handlePasswordChangeSuccess = (message: string) => {
    setSnackbar({
      open: true,
      message,
      severity: "success",
    });
  };

  const handlePasswordChangeError = (message: string) => {
    setSnackbar({
      open: true,
      message,
      severity: "error",
    });
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography variant="h1" component="h1" gutterBottom>
          Configuraciones
        </Typography>
      </Box>
      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h6" component="p" gutterBottom>
                  Apariencia
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Usa modo oscuro o claro. La preferencia se guarda en este
                  navegador.
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={mode === "dark"}
                    onChange={(_, checked) =>
                      setMode(checked ? "dark" : "light")
                    }
                    inputProps={{ "aria-label": "Modo oscuro" }}
                  />
                }
                label={mode === "dark" ? "Oscuro" : "Claro"}
              />
            </Stack>
          </CardContent>
        </Card>
        {/* Account Information */}
        <Typography variant="h2">Información de la cuenta</Typography>
        <Grid size={{ xs: 12, md: 12 }}>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                {/* User Info Row */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Stack spacing={1}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className="text-gray-500"
                      >
                        Nombre
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {user?.name}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Stack spacing={1}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className="text-gray-500"
                      >
                        Email
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {user?.email}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Stack spacing={1}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className="text-gray-500"
                      >
                        Empresa
                      </Typography>
                      <Box className="flex items-center gap-2">
                        {(myCompanyResp as any)?.imageS3Key ? (
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: 1,
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={addCacheBuster(
                                ensureDirectS3Url(
                                  `https://podscope.s3.amazonaws.com/${(myCompanyResp as any).imageS3Key}`,
                                ),
                              )}
                              alt="Empresa"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                            />
                          </Box>
                        ) : null}
                        <Typography variant="body1" fontWeight="medium">
                          {(myCompanyResp as any)?.name ||
                            user?.company ||
                            "No especificada"}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Stack spacing={1}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="text-gray-500"
                  >
                    Seguridad
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setShowPasswordDialog(true)}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Cambiar contraseña
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Typography variant="h2">Mis canales</Typography>

        {/* My Podcasts list linking to per-podcast invitations */}
        <Card>
          {myPodcastsLoading ? (
            <Box className="flex justify-center p-6">
              <CircularProgress />
            </Box>
          ) : myPodcastsError ? (
            <Alert severity="error">No se pudieron cargar tus podcasts</Alert>
          ) : myPodcasts.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              className="text-gray-500"
            >
              No tienes canales asignados.
            </Typography>
          ) : (
            <List sx={{ p: 3 }}>
              {myPodcasts.map((p, index) => (
                <React.Fragment key={p._id || p.slug || index}>
                  <PodcastRow podcast={p} />
                  {index < myPodcasts.length - 1 && <Divider sx={{ my: 1 }} />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Card>
      </Stack>

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={showPasswordDialog}
        onClose={() => setShowPasswordDialog(false)}
        onSuccess={handlePasswordChangeSuccess}
        onError={handlePasswordChangeError}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Settings;

// Renders a single podcast row with connected account chips
const PodcastRow: React.FC<{ podcast: Podcast }> = ({ podcast }) => {
  const { user } = useAuth();
  const { data: accounts } = useQuery<SocialAccount[]>({
    queryKey: ["settings-social-accounts", podcast._id],
    queryFn: () => apiService.getSocialAccountsByPodcast(podcast._id),
    enabled: !!podcast._id,
    staleTime: 1000 * 60 * 1, // 1 min
    gcTime: 1000 * 60 * 1, // 1 min
  });

  const platformStatus = useMemo(() => {
    const metaAccount = accounts?.some((a) => a.platform === "meta") || false;

    return user?.email === "test@podscope.co"
      ? {
          instagram: metaAccount,
          facebook: metaAccount,
        }
      : {
          instagram: metaAccount,
          facebook: metaAccount,
          youtube: accounts?.some((a) => a.platform === "youtube") || false,
          tiktok: accounts?.some((a) => a.platform === "tiktok") || false,
          spotify: accounts?.some((a) => a.platform === "spotify") || false,
        };
  }, [accounts, user?.email]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1,
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            overflow: "hidden",
            bgcolor: "grey.200",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "grey.500",
            fontSize: "0.875rem",
            flexShrink: 0,
          }}
        >
          {podcast.imageS3Key ? (
            <img
              src={addCacheBuster(
                ensureDirectS3Url(getS3Url(podcast.imageS3Key)),
              )}
              alt={podcast.title}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : podcast.imageUrl ? (
            <img
              src={addCacheBuster(podcast.imageUrl)}
              alt={podcast.title}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            (podcast.title?.charAt(0) || "P").toUpperCase()
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={podcast.title}
          >
            {podcast.title}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box className="flex items-center gap-2">
          {user?.email === "test@podscope.co" ? (
            <>
              <PlatformStatusIcon
                platform="instagram"
                active={platformStatus.instagram}
              />
              <PlatformStatusIcon
                platform="facebook"
                active={platformStatus.facebook}
              />
            </>
          ) : (
            <>
              <PlatformStatusIcon
                platform="instagram"
                active={platformStatus.instagram}
              />
              <PlatformStatusIcon
                platform="facebook"
                active={platformStatus.facebook}
              />
              <PlatformStatusIcon
                platform="youtube"
                active={platformStatus.youtube || false}
              />
              <PlatformStatusIcon
                platform="tiktok"
                active={platformStatus.tiktok || false}
              />
              <PlatformStatusIcon
                platform="spotify"
                active={platformStatus.spotify || false}
              />
            </>
          )}
        </Box>
        <Button
          component={RouterLink}
          to={`/settings/podcasts/${podcast.slug}`}
          size="small"
          variant="text"
          sx={settingsRowActionButtonSx}
        >
          Ver
        </Button>
      </Box>
    </Box>
  );
};

const PlatformStatusIcon: React.FC<{
  platform: "instagram" | "facebook" | "youtube" | "tiktok" | "spotify";
  active: boolean;
}> = ({ platform, active }) => {
  const iconColor = {
    instagram: "#E1306C",
    facebook: "#1877F2",
    youtube: "#FF0000",
    tiktok: "#000000",
    spotify: "#1DB954",
  }[platform];

  const PlatformIcon = ({
    size = "small",
  }: {
    size?: "small" | "inherit" | "large" | "medium";
  }) => {
    switch (platform) {
      case "youtube":
        return <YouTube fontSize={size} sx={{ color: iconColor }} />;
      case "instagram":
        return <Instagram fontSize={size} sx={{ color: iconColor }} />;
      case "facebook":
        return <Facebook fontSize={size} sx={{ color: iconColor }} />;
      case "tiktok":
        return <TikTokIcon fontSize={size} color={iconColor} />;
      case "spotify":
        return <SpotifyIcon fontSize={size} />;
      default:
        return <Audiotrack fontSize={size} sx={{ color: iconColor }} />;
    }
  };

  return (
    <Tooltip title={active ? "Conectado" : "No conectado"} arrow>
      <Box
        className="flex items-center"
        sx={{
          minWidth: 24,
          justifyContent: "flex-end",
          opacity: active ? 1 : 0.2,
          filter: active ? "none" : "grayscale(100%)",
        }}
      >
        <PlatformIcon />
      </Box>
    </Tooltip>
  );
};
